import { useMemo } from 'react';
import type { FirmDocumentCompany, FirmDocumentFund } from 'api';
import { EntityListPanel, EntityListPanelBadge, OverflowTooltip, Tooltip } from 'components/ui';
import type { EntityListPanelFilterOption, EntityListPanelItem } from 'components/ui';
import { getNumberValue } from 'utilities';
import { ALL_COMPANIES_PANEL_LABELS, ALL_COMPANIES_PANEL_SKELETON_ITEM_COUNT } from './AllCompaniesPanel.constants';
import type { AllCompaniesPanelProps } from './AllCompaniesPanel.props';
import { ALL_COMPANIES_PANEL_TEST_IDS } from './AllCompaniesPanel.testIds';

const getFundFilterLabel = (availableFunds: FirmDocumentFund[], selectedFundId?: number | null) => {
  const selectedFund = selectedFundId != null
    ? availableFunds.find(fund => fund.id === selectedFundId)
    : null;

  return selectedFund?.name ?? ALL_COMPANIES_PANEL_LABELS.filterByFund;
};

const getFundOverflowTooltipTitle = (funds: FirmDocumentFund[]) => (
  <>
    {funds.map((fund, index) => (
      <div key={fund.id}>
        {fund.name}
        {index < funds.length - 1 ? ',' : ''}
      </div>
    ))}
  </>
);

const AllCompaniesPanelFundBadge = ({ fund }: { fund: FirmDocumentFund }) => (
  <OverflowTooltip placement="bottom" title={fund.name}>
    <EntityListPanelBadge data-entity-list-panel-badge="true" data-entity-list-panel-badge-variant="label">
      {fund.name}
    </EntityListPanelBadge>
  </OverflowTooltip>
);

const AllCompaniesPanelFunds = ({ company }: { company: FirmDocumentCompany }) => {
  const [firstFund, ...remainingFunds] = company.funds ?? [];

  if (!firstFund) {
    return null;
  }

  if (!remainingFunds.length) {
    return <AllCompaniesPanelFundBadge fund={firstFund} />;
  }

  return (
    <>
      <AllCompaniesPanelFundBadge fund={firstFund} />
      <Tooltip placement="bottom" title={getFundOverflowTooltipTitle(remainingFunds)}>
        <EntityListPanelBadge
          data-entity-list-panel-badge="true"
          data-entity-list-panel-badge-variant="overflow"
          data-testid={ALL_COMPANIES_PANEL_TEST_IDS.companyFundOverflow(company.id)}>
          +{remainingFunds.length}
        </EntityListPanelBadge>
      </Tooltip>
    </>
  );
};

const getFilterOptions = (availableFunds: FirmDocumentFund[]): EntityListPanelFilterOption[] => [
  { label: ALL_COMPANIES_PANEL_LABELS.allFunds, value: null },
  ...[...availableFunds]
    .sort((fundA, fundB) => fundA.name.localeCompare(fundB.name))
    .map(fund => ({ label: fund.name, value: fund.id })),
];

const getCompanyItems = (companies: FirmDocumentCompany[]): EntityListPanelItem[] =>
  companies.map(company => ({
    ariaLabel: company.name,
    endContent: getNumberValue(company.document_count),
    id: company.id,
    label: company.name,
    metaContent: <AllCompaniesPanelFunds company={company} />,
  }));

const AllCompaniesPanel = ({
  availableFunds = [],
  companies,
  emptyDescription = ALL_COMPANIES_PANEL_LABELS.emptyDescription,
  emptyTitle = ALL_COMPANIES_PANEL_LABELS.emptyTitle,
  errorDescription = ALL_COMPANIES_PANEL_LABELS.errorDescription,
  errorTitle = ALL_COMPANIES_PANEL_LABELS.errorTitle,
  isError = false,
  isLoading = false,
  onCompanySelect,
  onFundFilterChange,
  onSearchChange,
  searchValue = '',
  selectedCompanyId,
  selectedFundId,
}: AllCompaniesPanelProps) => {
  const companyItems = useMemo(() => getCompanyItems(companies), [companies]);
  const filterOptions = useMemo(
    () => (availableFunds.length > 0 ? getFilterOptions(availableFunds) : []),
    [availableFunds]
  );

  return (
    <EntityListPanel
      emptyDescription={emptyDescription}
      emptyTitle={emptyTitle}
      errorDescription={errorDescription}
      errorTitle={errorTitle}
      filterAriaLabel={ALL_COMPANIES_PANEL_LABELS.filterByFund}
      filterEmptyLabel={ALL_COMPANIES_PANEL_LABELS.emptyFundFilterOptions}
      filterLabel={getFundFilterLabel(availableFunds, selectedFundId)}
      filterOptions={filterOptions}
      filterSearchPlaceholder={ALL_COMPANIES_PANEL_LABELS.fundFilterSearchPlaceholder}
      filterValue={selectedFundId ?? null}
      isError={isError}
      isLoading={isLoading}
      items={companyItems}
      onFilterChange={onFundFilterChange}
      onItemSelect={item => {
        const company = companies.find(companyItem => companyItem.id === item.id);
        if (company) {
          onCompanySelect?.(company);
        }
      }}
      onSearchChange={onSearchChange}
      searchCommitDelayMs={300}
      searchPlaceholder={ALL_COMPANIES_PANEL_LABELS.searchPlaceholder}
      searchValue={searchValue}
      scrollHintItemLabelPlural="companies"
      scrollHintItemLabelSingular="company"
      selectedItemId={selectedCompanyId}
      skeletonItemCount={ALL_COMPANIES_PANEL_SKELETON_ITEM_COUNT}
      testIds={{
        emptyState: ALL_COMPANIES_PANEL_TEST_IDS.emptyState,
        errorState: ALL_COMPANIES_PANEL_TEST_IDS.errorState,
        filterButton: ALL_COMPANIES_PANEL_TEST_IDS.fundFilterButton,
        filterMenu: ALL_COMPANIES_PANEL_TEST_IDS.fundFilterMenu,
        filterOption: ALL_COMPANIES_PANEL_TEST_IDS.fundFilterOption,
        filterSearchInput: ALL_COMPANIES_PANEL_TEST_IDS.fundFilterSearchInput,
        item: itemId => ALL_COMPANIES_PANEL_TEST_IDS.companyItem(Number(itemId)),
        list: ALL_COMPANIES_PANEL_TEST_IDS.list,
        root: ALL_COMPANIES_PANEL_TEST_IDS.root,
        searchInput: ALL_COMPANIES_PANEL_TEST_IDS.searchInput,
        skeleton: ALL_COMPANIES_PANEL_TEST_IDS.skeleton,
      }}
      title={ALL_COMPANIES_PANEL_LABELS.title}
    />
  );
};

export default AllCompaniesPanel;
